import React, { useContext, useEffect, useRef } from 'react';
import { WalkDataContext } from '../../hooks/walkDataContext';
import { Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUndo, faMinus, faPlus } from '@fortawesome/free-solid-svg-icons';

// Custom SVG Icon für Reverse/Forward
const ReverseIcon = ({ isReversed }) => (
  <svg
    viewBox="0 0 33 21"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      width: '1.3em',
      height: '1.3em',
      display: 'block',
      transform: isReversed ? 'rotate(180deg)' : 'none',
      transition: 'transform 0.3s ease'
    }}
    aria-hidden="true"
  >
    <path d="M10.736 9.846V4.22h13.713V0l8.063 7.034-8.063 7.033v-4.22H10.736zm11.04 1.3v5.628H8.064v4.219L0 13.96l8.063-7.033v4.22h13.714zM6.899.414l8.518-.036V1.82H6.898V.414zm10.744 0l3.614-.036V1.82h-3.614V.414z" fill="currentColor" />
  </svg>
);

const ButtonSpeedControlsAlt = ({
  className = ''
}) => {
  const { audioEffects, updateAudioEffect } = useContext(WalkDataContext);
  const liveRegionRef = useRef(null);

  // Geschwindigkeits-Konfiguration
  const MIN_SPEED = 0.1;
  const MAX_SPEED = 5;
  const NORMAL_SPEED = 1.0;
  const STEPS_COUNT = 10; // 10 Steps in jede Richtung von 1.0

  // Berechne Multiplikator für logarithmische Steps
  // Von 1.0 zu MAX_SPEED: 10 Steps
  // Von 1.0 zu MIN_SPEED: 10 Steps
  const STEP_FACTOR_UP = Math.pow(MAX_SPEED, 1 / STEPS_COUNT); // ~1.175
  const STEP_FACTOR_DOWN = Math.pow(MIN_SPEED, 1 / STEPS_COUNT); // ~0.794

  const currentSpeed = audioEffects.playbackRate || 1;
  const isReversed = audioEffects.isReversed || false;

  // Geschwindigkeit erhöhen
  const increaseSpeed = () => {
    const newSpeed = Math.min(MAX_SPEED, currentSpeed * STEP_FACTOR_UP);
    updateAudioEffect('playbackRate', parseFloat(newSpeed.toFixed(3)));
  };

  // Geschwindigkeit verringern
  const decreaseSpeed = () => {
    const newSpeed = Math.max(MIN_SPEED, currentSpeed * STEP_FACTOR_DOWN);
    updateAudioEffect('playbackRate', parseFloat(newSpeed.toFixed(3)));
  };

  // Reset auf normale Geschwindigkeit und Vorwärts
  const resetSpeed = () => {
    updateAudioEffect('playbackRate', NORMAL_SPEED);
    updateAudioEffect('isReversed', false);
  };

  // Toggle Reverse
  const toggleReverse = () => {
    updateAudioEffect('isReversed', !isReversed);
  };

  // Bestimme, ob wir am Minimum oder Maximum sind
  const isAtMax = currentSpeed >= MAX_SPEED - 0.01;
  const isAtMin = currentSpeed <= MIN_SPEED + 0.01;
  const isNormalSpeed = Math.abs(currentSpeed - 1) < 0.01 && !isReversed;

  // Screen Reader Ankündigung für Geschwindigkeitsänderungen
  const getSpeedAnnouncement = () => {
    const direction = isReversed ? 'Reverse' : 'Forward';
    return `Playing at ${currentSpeed.toFixed(1)}x speed, ${direction}`;
  };

  // Digitale Anzeige
  const getDisplayText = () => {
    const direction = isReversed ? 'Rev' : 'Fwd';
    return `${currentSpeed.toFixed(1)}x ${direction}`;
  };

  // Update live region for screen readers when speed changes
  useEffect(() => {
    if (liveRegionRef.current) {
      liveRegionRef.current.textContent = getSpeedAnnouncement();
    }
  }, [currentSpeed, isReversed]);

  return (
    <div className={`button-speed-controls-alt ${className}`}>
      {/* Live Region für Screen Reader Ankündigungen */}
      <div
        ref={liveRegionRef}
        className="sr-only"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      />

      <div
        className="speed-controls-compact"
        role="group"
        aria-label="Playback speed control with buttons"
      >
        {/* Reset Button */}
        <Button
          onClick={resetSpeed}
          disabled={isNormalSpeed}
          variant="success"
          size="sm"
          className="speed-btn speed-btn-reset"
          aria-label="Reset to normal speed (1x forward)"
          title="Reset"
        >
          <FontAwesomeIcon icon={faUndo} aria-hidden="true" />
        </Button>

        {/* Slower Button */}
        <Button
          onClick={decreaseSpeed}
          disabled={isAtMin}
          variant="warning"
          size="sm"
          className="speed-btn speed-btn-slower"
          aria-label={`Decrease speed. Current: ${getSpeedAnnouncement()}`}
          title="Slower"
        >
          <FontAwesomeIcon icon={faMinus} aria-hidden="true" />
        </Button>

        {/* Digital Display */}
        <div className="speed-display-compact" aria-hidden="true">
          <span className={`speed-value ${isReversed ? 'reverse' : 'forward'}`}>
            {getDisplayText()}
          </span>
        </div>

        {/* Faster Button */}
        <Button
          onClick={increaseSpeed}
          disabled={isAtMax}
          variant="primary"
          size="sm"
          className="speed-btn speed-btn-faster"
          aria-label={`Increase speed. Current: ${getSpeedAnnouncement()}`}
          title="Faster"
        >
          <FontAwesomeIcon icon={faPlus} aria-hidden="true" />
        </Button>

        {/* Reverse Toggle Button */}
        <Button
          onClick={toggleReverse}
          variant={isReversed ? 'danger' : 'secondary'}
          size="sm"
          className={`speed-btn speed-btn-reverse ${isReversed ? 'active' : ''}`}
          aria-label={`Reverse playback ${isReversed ? 'on' : 'off'}`}
          aria-pressed={isReversed}
          title="Reverse"
          style={{ padding: 0 }}
        >
          <ReverseIcon isReversed={isReversed} />
        </Button>
      </div>
    </div>
  );
};

export default ButtonSpeedControlsAlt;
